import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Zap, Infinity as InfinityIcon, History, FileText, Image as ImageIcon, AlignLeft, Camera } from "lucide-react";
import { Logo } from "@/components/Logo";

const FEATURES = [
  { icon: InfinityIcon, text: "Unlimited AI chat responses" },
  { icon: AlignLeft, text: "Longer, more detailed responses" },
  { icon: History, text: "Unlimited saved chat history" },
  { icon: FileText, text: "Resume and cover letter builder" },
  { icon: ImageIcon, text: "AI image generation chat responses" },
  { icon: Camera, text: "Photo & document tools" },
  { icon: Zap, text: "Priority new features" },
];

const PLANS = {
  monthly: { price: "$9.99", per: "/ month", note: "Billed monthly" },
  yearly: { price: "$99.99", per: "/ year", note: "$8.33/mo · billed yearly" },
};

export function UpgradeModal({ open, onClose, reason }) {
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState("monthly");

  const startCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, plan });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  const p = PLANS[plan];

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-md bg-black/70"
          onClick={onClose} data-testid="upgrade-modal">
          <motion.div initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: "spring", damping: 22, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[#15151d] border border-[#2a3040] rounded-2xl p-8 overflow-hidden">
            <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-[#FF5A00]/20 blur-3xl pointer-events-none" />
            <button data-testid="upgrade-close-button" onClick={onClose}
              className="absolute top-4 right-4 text-[#6b7280] hover:text-white transition-colors">
              <X size={20} />
            </button>

            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FF5A00] font-bold mb-3">
              <Zap size={14} /> Go Pro
            </div>
            <h2 className="font-display font-black text-3xl tracking-tight mb-4">
              Unlock <Logo size="text-3xl" /> Pro
            </h2>

            {/* Plan toggle */}
            <div className="grid grid-cols-2 gap-2 mb-5">
              <button data-testid="plan-monthly" onClick={() => setPlan("monthly")}
                className={`relative rounded-lg border px-3 py-3 text-left transition-all ${
                  plan === "monthly" ? "border-[#FF5A00] bg-[#FF5A00]/10" : "border-[#2a3040] hover:border-[#3a4050]"}`}>
                <span className="absolute -top-2 right-2 text-[9px] uppercase tracking-wider bg-[#FF5A00] text-[#0a0a0f] px-1.5 py-0.5 rounded font-bold">Popular</span>
                <div className="text-sm font-bold text-white">Monthly</div>
                <div className="text-xs text-[#9ca3af]">$9.99 / mo</div>
              </button>
              <button data-testid="plan-yearly" onClick={() => setPlan("yearly")}
                className={`relative rounded-lg border px-3 py-3 text-left transition-all ${
                  plan === "yearly" ? "border-[#FF5A00] bg-[#FF5A00]/10" : "border-[#2a3040] hover:border-[#3a4050]"}`}>
                <span className="absolute -top-2 right-2 text-[9px] uppercase tracking-wider bg-[#2a3040] text-[#9ca3af] px-1.5 py-0.5 rounded font-bold">Save 16%</span>
                <div className="text-sm font-bold text-white">Yearly</div>
                <div className="text-xs text-[#9ca3af]">$8.33 / mo</div>
              </button>
            </div>

            <div className="flex items-end gap-1 mb-1">
              <span className="font-display font-black text-5xl text-white">{p.price}</span>
              <span className="text-[#9ca3af] mb-1.5">{p.per}</span>
            </div>
            <p className="text-xs text-[#6b7280] mb-6">{p.note}</p>

            <div className="space-y-3 mb-7">
              {FEATURES.map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#FF5A00]/15 flex items-center justify-center shrink-0">
                    <f.icon size={16} className="text-[#FF5A00]" />
                  </div>
                  <span className="text-sm text-[#f9fafb]">{f.text}</span>
                </div>
              ))}
            </div>

            <button data-testid="upgrade-checkout-button" onClick={startCheckout} disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-6 py-3.5 hover:bg-[#FF7A33] transition-colors disabled:opacity-60">
              {loading ? "Redirecting to checkout…" : `Upgrade — ${p.price} ${p.per}`}
            </button>
            <p className="text-center text-xs text-[#6b7280] mt-3">
              Secure checkout by Stripe · Cancel anytime · Test card 4242 4242 4242 4242
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
